import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { FileText, Globe, Clock, CheckCircle, ChevronRight, Send, Loader2, Briefcase, GraduationCap, Plane } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VISA_COUNTRIES = [
  { name: 'USA', flag: '🇺🇸', processing: '15-30 days', types: ['Tourist (B1/B2)', 'Business', 'Student (F1)'] },
  { name: 'UK', flag: '🇬🇧', processing: '15-20 days', types: ['Tourist', 'Business', 'Student (Tier 4)'] },
  { name: 'UAE', flag: '🇦🇪', processing: '3-5 days', types: ['Tourist', 'Transit', 'Business'] },
  { name: 'Thailand', flag: '🇹🇭', processing: '5-7 days', types: ['Tourist', 'Transit', 'Business'] },
  { name: 'Europe (Schengen)', flag: '🇪🇺', processing: '10-15 days', types: ['Tourist', 'Business', 'Student'] },
  { name: 'Australia', flag: '🇦🇺', processing: '20-30 days', types: ['Tourist', 'Business', 'Student (subclass 500)'] },
  { name: 'Canada', flag: '🇨🇦', processing: '20-30 days', types: ['Tourist', 'Business', 'Student'] },
  { name: 'Japan', flag: '🇯🇵', processing: '5-7 days', types: ['Tourist', 'Business', 'Transit'] },
  { name: 'Singapore', flag: '🇸🇬', processing: '3-5 days', types: ['Tourist', 'Business', 'Transit'] },
  { name: 'New Zealand', flag: '🇳🇿', processing: '15-25 days', types: ['Tourist', 'Business', 'Student'] },
  { name: 'Malaysia', flag: '🇲🇾', processing: '3-5 days', types: ['Tourist', 'eNTRI', 'Business'] },
  { name: 'South Korea', flag: '🇰🇷', processing: '7-10 days', types: ['Tourist', 'Business', 'Student'] },
];

const DOCUMENTS = [
  'Valid passport (6+ months validity)',
  'Recent passport-size photographs',
  'Completed visa application form',
  'Travel itinerary & flight booking',
  'Hotel reservation / accommodation proof',
  'Bank statements (last 6 months)',
  'Employment / business proof',
  'Travel insurance certificate',
];

export default function VisaPage() {
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [form, setForm] = useState({ name: '', phone: '', destination: '', travel_date: '', message: '' });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/service-inquiry`, { ...form, service_type: 'visa' });
      setSuccess(true);
      setForm({ name: '', phone: '', destination: '', travel_date: '', message: '' });
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  return (
    <div data-testid="visa-page" className="min-h-screen bg-white pt-20">
      {/* Hero */}
      <div className="bg-navy py-16">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <p className="font-body text-sm tracking-[0.2em] uppercase text-gold font-semibold mb-3">Visa Assistance</p>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-4">Visa Services for 120+ Countries</h1>
          <p className="font-body text-base text-gray-400 max-w-2xl mx-auto">Fast, reliable, and hassle-free visa assistance worldwide. We handle the paperwork, you plan the adventure.</p>
        </div>
      </div>

      {/* Visa Types */}
      <div className="section-padding bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <p className="font-body text-sm tracking-[0.2em] uppercase text-gold font-semibold mb-3">We Process</p>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-navy">All Visa Types</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-16">
            {[
              { icon: <Plane className="w-8 h-8" />, title: 'Tourist Visa', desc: 'For leisure travel, holidays, and sightseeing across the world.' },
              { icon: <Briefcase className="w-8 h-8" />, title: 'Business Visa', desc: 'For meetings, conferences, and business activities abroad.' },
              { icon: <GraduationCap className="w-8 h-8" />, title: 'Student Visa', desc: 'For pursuing education and academic programs internationally.' },
            ].map(v => (
              <div key={v.title} className="text-center p-8 rounded-3xl bg-navy-50 border border-gray-100 hover:-translate-y-2 transition-transform duration-300">
                <div className="w-16 h-16 bg-gold/10 rounded-2xl flex items-center justify-center mx-auto mb-5 text-gold">{v.icon}</div>
                <h3 className="font-heading text-xl font-semibold text-navy mb-2">{v.title}</h3>
                <p className="font-body text-sm text-gray-500">{v.desc}</p>
              </div>
            ))}
          </div>

          {/* Country Grid */}
          <div className="text-center mb-10">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-navy mb-4">Popular Countries</h2>
            <p className="font-body text-base text-gray-500">Select a country to see visa details</p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 mb-12">
            {VISA_COUNTRIES.map(c => (
              <button key={c.name} onClick={() => setSelectedCountry(c)} data-testid={`visa-country-${c.name.toLowerCase().replace(/[^a-z]/g, '')}`}
                className={`p-5 rounded-2xl border text-center hover:-translate-y-1 transition-all duration-300 ${selectedCountry?.name === c.name ? 'border-gold bg-gold/5 shadow-md' : 'border-gray-100 bg-white hover:border-gold/30'}`}>
                <span className="text-3xl mb-2 block">{c.flag}</span>
                <h4 className="font-heading text-sm font-semibold text-navy">{c.name}</h4>
                <p className="font-body text-xs text-gray-400 mt-1"><Clock className="w-3 h-3 inline mr-1" />{c.processing}</p>
              </button>
            ))}
          </div>

          {/* Selected Country Details */}
          {selectedCountry && (
            <div data-testid="visa-country-details" className="p-8 rounded-3xl bg-navy-50 border border-gray-100 mb-12 animate-fade-up">
              <div className="flex items-center gap-4 mb-6">
                <span className="text-5xl">{selectedCountry.flag}</span>
                <div>
                  <h3 className="font-heading text-2xl font-bold text-navy">{selectedCountry.name} Visa</h3>
                  <p className="font-body text-sm text-gray-500">Processing: {selectedCountry.processing}</p>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-heading text-base font-semibold text-navy mb-3">Available Visa Types</h4>
                  <div className="space-y-2">
                    {selectedCountry.types.map(t => (
                      <div key={t} className="flex items-center gap-2 font-body text-sm text-gray-600">
                        <CheckCircle className="w-4 h-4 text-gold shrink-0" /> {t}
                      </div>
                    ))}
                  </div>
                </div>
                <div>
                  <h4 className="font-heading text-base font-semibold text-navy mb-3">Required Documents</h4>
                  <div className="space-y-2">
                    {DOCUMENTS.slice(0, 5).map(d => (
                      <div key={d} className="flex items-center gap-2 font-body text-xs text-gray-600">
                        <FileText className="w-3 h-3 text-gold shrink-0" /> {d}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Required Documents */}
          <div className="mb-16">
            <h2 className="font-heading text-2xl font-bold text-navy mb-6">General Documents Required</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {DOCUMENTS.map((d, i) => (
                <div key={i} className="flex items-center gap-3 p-4 rounded-2xl bg-navy-50 border border-gray-100">
                  <div className="w-8 h-8 bg-gold/10 rounded-xl flex items-center justify-center shrink-0">
                    <FileText className="w-4 h-4 text-gold" />
                  </div>
                  <span className="font-body text-sm text-navy">{d}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Inquiry Form */}
      <div className="section-padding bg-navy">
        <div className="max-w-2xl mx-auto px-6">
          <div className="text-center mb-10">
            <p className="font-body text-sm tracking-[0.2em] uppercase text-gold font-semibold mb-3">Apply Now</p>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-white">Get Visa Assistance</h2>
          </div>

          {success ? (
            <div data-testid="visa-form-success" className="text-center p-10 rounded-3xl bg-white/10 border border-white/20">
              <CheckCircle className="w-16 h-16 text-gold mx-auto mb-4" />
              <h3 className="font-heading text-xl font-semibold text-white mb-2">Inquiry Submitted!</h3>
              <p className="font-body text-sm text-gray-400">Our visa expert will contact you within 24 hours.</p>
              <button onClick={() => setSuccess(false)} className="mt-4 font-body text-sm text-gold hover:underline">Submit another inquiry</button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} data-testid="visa-form" className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <input type="text" required value={form.name} onChange={e => setForm({...form, name: e.target.value})}
                  data-testid="visa-name" placeholder="Full Name"
                  className="px-4 py-3 rounded-2xl bg-white/10 border border-white/20 font-body text-sm text-white placeholder:text-gray-400 focus:outline-none focus:border-gold" />
                <input type="tel" required value={form.phone} onChange={e => setForm({...form, phone: e.target.value})}
                  data-testid="visa-phone" placeholder="Phone Number"
                  className="px-4 py-3 rounded-2xl bg-white/10 border border-white/20 font-body text-sm text-white placeholder:text-gray-400 focus:outline-none focus:border-gold" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <input type="text" value={form.destination} onChange={e => setForm({...form, destination: e.target.value})}
                  data-testid="visa-destination" placeholder="Country / Destination"
                  className="px-4 py-3 rounded-2xl bg-white/10 border border-white/20 font-body text-sm text-white placeholder:text-gray-400 focus:outline-none focus:border-gold" />
                <input type="date" value={form.travel_date} onChange={e => setForm({...form, travel_date: e.target.value})}
                  data-testid="visa-date" 
                  className="px-4 py-3 rounded-2xl bg-white/10 border border-white/20 font-body text-sm text-white placeholder:text-gray-400 focus:outline-none focus:border-gold" />
              </div>
              <textarea rows={3} value={form.message} onChange={e => setForm({...form, message: e.target.value})}
                data-testid="visa-message" placeholder="Any specific requirements..."
                className="w-full px-4 py-3 rounded-2xl bg-white/10 border border-white/20 font-body text-sm text-white placeholder:text-gray-400 focus:outline-none focus:border-gold resize-none" />
              <button type="submit" disabled={loading} data-testid="visa-submit"
                className="w-full flex items-center justify-center gap-2 bg-gold text-white hover:bg-gold-dark rounded-full py-3.5 font-body font-semibold transition-all hover:-translate-y-0.5 disabled:opacity-50">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4" /> Get Visa Assistance</>}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
